import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area
} from "recharts";
import {
  TrendingUp, TrendingDown, Minus, Users, Activity, Clock,
  AlertTriangle, Shield, BookOpen, Target, ChevronRight,
  Package, ChevronDown, ChevronUp, BarChart3
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WOUND_COLORS = { ulcera_venosa: "#2d6a4f", pe_diabetico: "#e76f51", lesao_por_pressao: "#457b9d" };
const WOUND_LABELS = { ulcera_venosa: "Úlcera Venosa", pe_diabetico: "Pé Diabético", lesao_por_pressao: "Lesão por Pressão" };
const RISK_COLORS = { critico: "#ef4444", alto: "#f97316", medio: "#eab308", baixo: "#22c55e" };
const GLYCEMIC_COLORS = ["#22c55e", "#ef4444", "#94a3b8"];
const ROLE_LABELS = { medico: "Médico(a)", enfermeiro: "Enfermeiro(a)", tecnico: "Técnico(a)" };

const CATEGORY_LABELS = {
  coberturas_primarias: "Cobertura Primária",
  coberturas_secundarias: "Cobertura Secundária",
  limpeza: "Limpeza",
  desbridamento: "Desbridamento",
  fixacao: "Fixação",
  outros: "Outros",
};
const CATEGORY_COLORS_MAP = {
  coberturas_primarias: "bg-blue-100 text-blue-700",
  coberturas_secundarias: "bg-sky-100 text-sky-700",
  limpeza: "bg-green-100 text-green-700",
  desbridamento: "bg-amber-100 text-amber-700",
  fixacao: "bg-purple-100 text-purple-700",
  outros: "bg-gray-100 text-gray-600",
};

function fmtMonth(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const names = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  return `${names[parseInt(m,10)-1]}/${y.slice(2)}`;
}

const CardStat = ({ label, value, sub, icon: Icon, color, testId }) => (
  <div data-testid={testId} className={`bg-white rounded-xl border border-border p-5 animate-fade-in`}>
    <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center mb-3`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <p className="text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
    {sub && <p className="text-xs text-muted-foreground/70 mt-0.5">{sub}</p>}
  </div>
);

function TrendBadge({ trend, pct }) {
  if (trend === "crescente") return (
    <span className="flex items-center gap-0.5 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
      <TrendingUp className="w-3 h-3" />+{Math.abs(pct)}%
    </span>
  );
  if (trend === "decrescente") return (
    <span className="flex items-center gap-0.5 text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
      <TrendingDown className="w-3 h-3" />-{Math.abs(pct)}%
    </span>
  );
  return (
    <span className="flex items-center gap-0.5 text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
      <Minus className="w-3 h-3" />Estável
    </span>
  );
}

function ForecastCard({ item, expanded, onToggle }) {
  const historyBars = (item.months_labels || []).map(m => ({
    month: fmtMonth(m),
    qty: item.monthly_history?.[m] || 0,
  }));
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-3 p-4 bg-white">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <p className="text-sm font-semibold text-foreground truncate">{item.material_name}</p>
              <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${CATEGORY_COLORS_MAP[item.category] || "bg-gray-100 text-gray-600"}`}>
                {CATEGORY_LABELS[item.category] || item.category}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">Média 3m: {item.avg_monthly} {item.unit} · Mês atual: {item.current_month_qty} {item.unit}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-lg font-bold text-primary" style={{ fontFamily: "Manrope" }}>{item.forecast_next_month}</p>
            <p className="text-xs text-muted-foreground">{item.unit} / próx. mês</p>
          </div>
          <TrendBadge trend={item.trend} pct={Math.abs(item.trend_pct)} />
          <button onClick={onToggle} className="p-1 rounded-lg hover:bg-secondary transition-all">
            {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border p-4 bg-secondary/30 space-y-4">
          {/* Mini bar chart: histórico 3 meses */}
          {historyBars.some(b => b.qty > 0) && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">Histórico de consumo (3 meses)</p>
              <ResponsiveContainer width="100%" height={80}>
                <BarChart data={historyBars} margin={{ top: 0, right: 0, left: -30, bottom: 0 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="qty" name={item.unit} fill="hsl(var(--primary))" radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {/* Distribuição por unidade */}
          {item.by_unit?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">Previsão por Unidade de Saúde</p>
              <div className="space-y-2">
                {item.by_unit.map((u, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <p className="text-xs text-foreground w-40 truncate">{u.unit_name}</p>
                    <div className="flex-1 h-4 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${u.share_pct}%` }}
                      />
                    </div>
                    <p className="text-xs font-semibold text-foreground w-16 text-right flex-shrink-0">{u.forecast_qty} {item.unit}</p>
                    <p className="text-xs text-muted-foreground w-10 flex-shrink-0">{u.share_pct}%</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MunicipalDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("overview");
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    axios.get(`${API}/dashboard/municipal`, { withCredentials: true })
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeSection === "materiais" && !forecast && !forecastLoading) {
      setForecastLoading(true);
      axios.get(`${API}/municipality-materials/forecast`, { withCredentials: true })
        .then(r => setForecast(r.data))
        .catch(() => setForecast({ forecast: [], unit_summary: [], total_materials_tracked: 0, months_labels: [] }))
        .finally(() => setForecastLoading(false));
    }
  }, [activeSection, forecast, forecastLoading]);

  const SECTIONS = [
    { id: "overview", label: "Visão Geral" },
    { id: "resources", label: "Recursos" },
    { id: "materiais", label: "Materiais" },
    { id: "glycemic", label: "Glicemia" },
    { id: "education", label: "Educação" },
  ];

  if (loading) return (
    <Layout>
      <div className="space-y-4">
        <div className="h-8 w-48 bg-muted rounded-lg animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 rounded-xl bg-white border border-border animate-pulse" />)}
        </div>
      </div>
    </Layout>
  );

  if (!stats) return null;

  const woundTypeData = Object.entries(stats.wounds_by_type || {}).map(([k, v]) => ({
    name: WOUND_LABELS[k] || k, value: v, color: WOUND_COLORS[k]
  }));

  const riskData = Object.entries(stats.wounds_by_risk || {}).map(([k, v]) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1), value: v, color: RISK_COLORS[k]
  }));

  const glycemicData = [
    { name: "Adequado", value: stats.glycemic_control_stats?.adequado || 0 },
    { name: "Inadequado", value: stats.glycemic_control_stats?.inadequado || 0 },
    { name: "Desconhecido", value: stats.glycemic_control_stats?.desconhecido || 0 },
  ].filter(d => d.value > 0);

  const inadequateGlycemic = stats.glycemic_control_stats?.inadequado || 0;
  const diabeticTotal = stats.diabetic_patients_total || 1;
  const inadequatePct = Math.round((inadequateGlycemic / diabeticTotal) * 100);

  // Dados de materiais para gráfico de top consumo (3 meses)
  const topMaterialsChart = (forecast?.forecast || []).slice(0, 10).map(f => ({
    name: f.material_name.length > 22 ? f.material_name.slice(0, 20) + "…" : f.material_name,
    total3m: f.total_3mo,
    prev: f.forecast_next_month,
  }));

  const unitSummaryChart = (forecast?.unit_summary || []).map(u => ({
    name: u.unit_name.length > 18 ? u.unit_name.slice(0, 16) + "…" : u.unit_name,
    forecast: u.forecast_total,
    historico: Math.round(u.historical_total / 3),
  }));

  return (
    <Layout>
      <div data-testid="municipal-dashboard" className="space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>Painel Municipal</h1>
            <p className="text-sm text-muted-foreground">Indicadores para tomada de decisão — {new Date().toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}</p>
          </div>
        </div>

        {/* Section tabs */}
        <div className="flex gap-1 bg-secondary p-1 rounded-xl w-fit flex-wrap animate-fade-in">
          {SECTIONS.map(({ id, label }) => (
            <button key={id} onClick={() => setActiveSection(id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeSection === id ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`section-tab-${id}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ══ OVERVIEW ══ */}
        {activeSection === "overview" && (
          <div className="space-y-5">
            {stats.patients_without_recent_evolution > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3 animate-fade-in" data-testid="evolution-gap-alert">
                <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-yellow-800">
                    {stats.patients_without_recent_evolution} lesão(ões) sem evolução há mais de 14 dias
                  </p>
                  <p className="text-xs text-yellow-700">Ação recomendada: verificar acompanhamento das equipes</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <CardStat label="Pacientes" value={stats.total_patients} icon={Users} color="bg-primary" testId="stat-total-patients" />
              <CardStat label="Lesões Ativas" value={stats.total_active_wounds} sub={`${stats.healing_rate}% cicatrizaram este mês`} icon={Activity} color="bg-accent" testId="stat-active-wounds" />
              <CardStat label="Interconsultas Pend." value={stats.pending_teleconsultations} icon={Clock} color="bg-yellow-500" testId="stat-telecons" />
              <CardStat label="Evoluções/Mês" value={stats.evolutions_this_month} icon={TrendingUp} color="bg-blue-500" testId="stat-evolutions" />
            </div>

            {/* Materiais resumo no overview */}
            {stats.total_items_month > 0 && (
              <div className="bg-white rounded-xl border border-border p-5 animate-fade-in" data-testid="materials-overview-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-emerald-600" />
                    <h3 className="text-sm font-semibold" style={{ fontFamily: "Manrope" }}>Materiais este Mês</h3>
                  </div>
                  <button onClick={() => setActiveSection("materiais")} className="text-xs text-primary hover:underline flex items-center gap-1">
                    Ver previsão <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-2xl font-bold mb-2">{stats.total_items_month} <span className="text-sm font-normal text-muted-foreground">itens</span></p>
                <div className="flex flex-wrap gap-2">
                  {(stats.top_materials_month || []).slice(0, 4).map((m, i) => (
                    <span key={i} className="text-xs bg-secondary px-2 py-1 rounded-lg text-foreground">
                      {m.material_name}: <strong>{m.total}</strong> {m.unit}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-border p-5">
                <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Lesões por Tipo</h3>
                {woundTypeData.every(d => d.value === 0) ? (
                  <p className="text-xs text-muted-foreground text-center py-6">Nenhuma lesão ativa</p>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={woundTypeData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" label={({ name, value }) => `${value}`}>
                        {woundTypeData.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [v, n]} />
                      <Legend formatter={(v) => <span style={{ fontSize: 11 }}>{v}</span>} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

              <div className="bg-white rounded-xl border border-border p-5">
                <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Distribuição por Risco</h3>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={riskData} margin={{ top: 0, right: 0, left: -25, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[4,4,0,0]}>
                      {riskData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-border p-5">
              <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Tendência de Evoluções (6 meses)</h3>
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={stats.evolution_trend} margin={{ top: 5, right: 10, left: -25, bottom: 0 }}>
                  <defs>
                    <linearGradient id="evoGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="evolutions" stroke="hsl(var(--primary))" fill="url(#evoGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {stats.recent_alerts?.length > 0 && (
              <div className="bg-white rounded-xl border border-border p-5">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-destructive" />
                  <h3 className="text-sm font-semibold" style={{ fontFamily: "Manrope" }}>Alertas Recentes</h3>
                </div>
                <div className="space-y-2">
                  {stats.recent_alerts.slice(0, 5).map(a => (
                    <div key={a.wound_id} onClick={() => navigate(`/wounds/${a.wound_id}`)}
                      className="flex items-center justify-between p-3 rounded-lg border border-border/50 cursor-pointer hover:bg-secondary/50 transition-all"
                      data-testid={`alert-row-${a.wound_id}`}
                    >
                      <div>
                        <p className="text-sm font-medium">{a.patient_name}</p>
                        <p className="text-xs text-muted-foreground">{WOUND_LABELS[a.wound_type]}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full risk-${a.risk_level}`}>
                          {a.risk_level?.charAt(0).toUpperCase() + a.risk_level?.slice(1)}
                        </span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ RESOURCES ══ */}
        {activeSection === "resources" && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <CardStat label="Cicatrizaram este mês" value={stats.healed_this_month || 0} sub={`Taxa: ${stats.healing_rate}%`} icon={Shield} color="bg-green-500" testId="stat-healed" />
              <CardStat label="Sem evolução (14d+)" value={stats.patients_without_recent_evolution} sub="Precisam de acompanhamento" icon={Clock} color="bg-orange-500" testId="stat-no-evolution" />
            </div>

            {stats.patients_by_unit?.length > 0 && (
              <div className="bg-white rounded-xl border border-border p-5">
                <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Pacientes por Unidade de Saúde</h3>
                <ResponsiveContainer width="100%" height={Math.max(160, stats.patients_by_unit.length * 40)}>
                  <BarChart data={stats.patients_by_unit} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="unit" type="category" tick={{ fontSize: 10 }} width={100} />
                    <Tooltip />
                    <Bar dataKey="patients" name="Pacientes" fill="hsl(var(--primary))" radius={[0,4,4,0]} />
                    <Bar dataKey="wounds" name="Lesões Ativas" fill="hsl(var(--accent))" radius={[0,4,4,0]} />
                    <Legend formatter={(v) => <span style={{ fontSize: 11 }}>{v}</span>} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {stats.professional_workload?.length > 0 && (
              <div className="bg-white rounded-xl border border-border p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-semibold" style={{ fontFamily: "Manrope" }}>Carga de Trabalho Profissional</h3>
                </div>
                <div className="space-y-2">
                  {stats.professional_workload.map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50" data-testid={`workload-${i}`}>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                          {p.name?.[0] || "P"}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{p.name}</p>
                          <p className="text-xs text-muted-foreground">{ROLE_LABELS[p.role] || p.role}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-foreground">{p.patients}</p>
                        <p className="text-xs text-muted-foreground">pacientes</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ MATERIAIS ══ */}
        {activeSection === "materiais" && (
          <div className="space-y-5" data-testid="section-materiais">
            {forecastLoading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => <div key={i} className="h-24 bg-white border border-border rounded-xl animate-pulse" />)}
              </div>
            ) : !forecast || forecast.total_materials_tracked === 0 ? (
              <div className="bg-white rounded-xl border border-border p-12 text-center">
                <Package className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-base font-semibold text-foreground">Sem dados de consumo de materiais</p>
                <p className="text-xs text-muted-foreground mt-1">Registre materiais utilizados nas evoluções para visualizar o consumo e a previsão de compras.</p>
              </div>
            ) : (
              <>
                {/* Cards de resumo */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl border border-border p-4" data-testid="mat-stat-tracked">
                    <p className="text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>{forecast.total_materials_tracked}</p>
                    <p className="text-xs text-muted-foreground mt-1">Materiais rastreados</p>
                  </div>
                  <div className="bg-white rounded-xl border border-border p-4" data-testid="mat-stat-items-month">
                    <p className="text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>{stats.total_items_month || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Itens usados este mês</p>
                  </div>
                  <div className="bg-white rounded-xl border border-border p-4" data-testid="mat-stat-forecast-total">
                    <p className="text-2xl font-bold text-primary" style={{ fontFamily: "Manrope" }}>
                      {forecast.forecast.reduce((s, f) => s + f.forecast_next_month, 0)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Prev. próximo mês (total)</p>
                  </div>
                </div>

                {/* Gráfico: Top materiais — consumo 3 meses vs previsão */}
                {topMaterialsChart.length > 0 && (
                  <div className="bg-white rounded-xl border border-border p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <BarChart3 className="w-4 h-4 text-primary" />
                      <h3 className="text-sm font-semibold" style={{ fontFamily: "Manrope" }}>Consumo (3 meses) vs Previsão Próximo Mês</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={Math.max(180, topMaterialsChart.length * 36)}>
                      <BarChart data={topMaterialsChart} layout="vertical" margin={{ top: 0, right: 30, left: 10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis type="number" tick={{ fontSize: 10 }} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={130} />
                        <Tooltip />
                        <Bar dataKey="total3m" name="Total 3 meses" fill="hsl(var(--primary))" radius={[0,4,4,0]} opacity={0.75} />
                        <Bar dataKey="prev" name="Prev. próx. mês" fill="#10b981" radius={[0,4,4,0]} />
                        <Legend formatter={(v) => <span style={{ fontSize: 11 }}>{v}</span>} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Alocação por unidade de saúde */}
                {unitSummaryChart.length > 0 && unitSummaryChart.some(u => u.forecast > 0) && (
                  <div className="bg-white rounded-xl border border-border p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Target className="w-4 h-4 text-emerald-600" />
                      <h3 className="text-sm font-semibold" style={{ fontFamily: "Manrope" }}>Previsão de Demanda por Unidade</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={Math.max(120, unitSummaryChart.length * 48)}>
                      <BarChart data={unitSummaryChart} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis type="number" tick={{ fontSize: 10 }} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={110} />
                        <Tooltip />
                        <Bar dataKey="historico" name="Média mensal (histórico)" fill="hsl(var(--primary))" radius={[0,4,4,0]} opacity={0.6} />
                        <Bar dataKey="forecast" name="Prev. próx. mês" fill="#10b981" radius={[0,4,4,0]} />
                        <Legend formatter={(v) => <span style={{ fontSize: 11 }}>{v}</span>} />
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="mt-4 space-y-2">
                      {forecast.unit_summary.map((u, i) => {
                        const grandTotal = forecast.unit_summary.reduce((s, x) => s + x.forecast_total, 0) || 1;
                        const sharePct = Math.round((u.forecast_total / grandTotal) * 100);
                        return (
                          <div key={i} className="flex items-center gap-3">
                            <p className="text-xs font-medium text-foreground w-36 truncate">{u.unit_name}</p>
                            <div className="flex-1 h-3 bg-secondary rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${sharePct}%` }} />
                            </div>
                            <p className="text-xs font-semibold text-emerald-700 w-10 text-right">{sharePct}%</p>
                            <p className="text-xs text-muted-foreground w-20 text-right">{u.forecast_total} itens</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Tabela detalhada de previsão por material */}
                <div className="bg-white rounded-xl border border-border p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <h3 className="text-sm font-semibold" style={{ fontFamily: "Manrope" }}>Previsão de Compras — Próximo Mês</h3>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">
                    Previsão calculada com média ponderada dos últimos 3 meses (pesos: mês recente = maior peso). Clique em cada material para ver detalhes por unidade.
                  </p>
                  <div className="space-y-2">
                    {forecast.forecast.map(item => (
                      <ForecastCard
                        key={item.material_id}
                        item={item}
                        expanded={!!expandedItems[item.material_id]}
                        onToggle={() => setExpandedItems(p => ({ ...p, [item.material_id]: !p[item.material_id] }))}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ══ GLYCEMIC ══ */}
        {activeSection === "glycemic" && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <CardStat label="Pacientes Diabéticos" value={stats.diabetic_patients_total || 0} icon={Activity} color="bg-primary" testId="stat-diabetic" />
              <CardStat
                label="Controle Inadequado"
                value={`${inadequatePct}%`}
                sub={`${inadequateGlycemic} pacientes`}
                icon={AlertTriangle}
                color={inadequatePct > 40 ? "bg-destructive" : "bg-orange-500"}
                testId="stat-inadequate"
              />
            </div>

            {glycemicData.length > 0 ? (
              <div className="bg-white rounded-xl border border-border p-5">
                <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Distribuição do Controle Glicêmico</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={glycemicData} cx="50%" cy="50%" outerRadius={85} dataKey="value" label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                      {glycemicData.map((_, i) => <Cell key={i} fill={GLYCEMIC_COLORS[i]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-border p-12 text-center">
                <Activity className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Nenhum dado de controle glicêmico registrado</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Cadastre pacientes com Diabetes Mellitus e informe o controle glicêmico</p>
              </div>
            )}

            {inadequatePct > 30 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="glycemic-alert-box">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-800">Atenção: {inadequatePct}% dos diabéticos com controle inadequado</p>
                    <p className="text-xs text-red-700 mt-1">
                      Recomendação: reforçar acompanhamento nutricional, ajuste de dose de insulina/hipoglicemiante, e ações educativas.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ EDUCATION ══ */}
        {activeSection === "education" && (
          <div className="space-y-5">
            <div className="bg-white rounded-xl border border-border p-5">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-primary" />
                <h3 className="text-sm font-semibold" style={{ fontFamily: "Manrope" }}>Medidas Educativas Recomendadas</h3>
              </div>
              <div className="space-y-3">
                {[
                  {
                    title: "Controle Glicêmico", condition: inadequateGlycemic > 0,
                    count: inadequateGlycemic, icon: Activity, color: "text-red-600 bg-red-50 border-red-100",
                    action: "Grupo de educação em diabetes: alimentação saudável, atividade física, monitoramento da glicemia."
                  },
                  {
                    title: "Prevenção de Lesão por Pressão",
                    condition: (stats.wounds_by_type?.lesao_por_pressao || 0) > 0,
                    count: stats.wounds_by_type?.lesao_por_pressao || 0, icon: Shield, color: "text-blue-600 bg-blue-50 border-blue-100",
                    action: "Treinamento de cuidadores: reposicionamento, uso de coxins, inspeção diária da pele."
                  },
                  {
                    title: "Pé Diabético",
                    condition: (stats.wounds_by_type?.pe_diabetico || 0) > 0,
                    count: stats.wounds_by_type?.pe_diabetico || 0, icon: Activity, color: "text-orange-600 bg-orange-50 border-orange-100",
                    action: "Inspeção diária dos pés, calçado adequado, hidratação, corte correto das unhas."
                  },
                  {
                    title: "Monitoramento Irregular",
                    condition: stats.patients_without_recent_evolution > 0,
                    count: stats.patients_without_recent_evolution, icon: Clock, color: "text-yellow-600 bg-yellow-50 border-yellow-100",
                    action: "Intensificar visitas domiciliares do ACS. Programar interconsulta de acompanhamento."
                  },
                ].filter(item => item.condition).map((item, i) => (
                  <div key={i} className={`flex items-start gap-3 p-4 rounded-xl border ${item.color}`} data-testid={`education-item-${i}`}>
                    <item.icon className={`w-5 h-5 ${item.color.split(" ")[0]} flex-shrink-0 mt-0.5`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold">{item.title}</p>
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-white">{item.count}</span>
                      </div>
                      <p className="text-xs mt-1 opacity-80">{item.action}</p>
                    </div>
                  </div>
                ))}
                {![inadequateGlycemic > 0, (stats.wounds_by_type?.lesao_por_pressao || 0) > 0, (stats.wounds_by_type?.pe_diabetico || 0) > 0, stats.patients_without_recent_evolution > 0].some(Boolean) && (
                  <div className="text-center py-8">
                    <Shield className="w-10 h-10 text-green-400 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Nenhuma medida educativa urgente identificada</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-border p-5">
              <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Fatores de Risco na População</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Úlcera Venosa", value: stats.wounds_by_type?.ulcera_venosa || 0, color: "bg-green-100 text-green-800" },
                  { label: "Pé Diabético", value: stats.wounds_by_type?.pe_diabetico || 0, color: "bg-orange-100 text-orange-800" },
                  { label: "Lesão por Pressão", value: stats.wounds_by_type?.lesao_por_pressao || 0, color: "bg-blue-100 text-blue-800" },
                  { label: "Casos Críticos", value: stats.wounds_by_risk?.critico || 0, color: "bg-red-100 text-red-800" },
                ].map(({ label, value, color }) => (
                  <div key={label} className={`flex items-center justify-between p-3 rounded-xl ${color}`}>
                    <p className="text-xs font-medium">{label}</p>
                    <span className="text-lg font-bold">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
